/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Layout from '../common/components/Layout';
import 'react-toastify/dist/ReactToastify.css';

import {
  fetchAllSyncConfigs,
  createSyncConfig,
  updateSyncConfig,
  deleteSyncConfig,
  runMasterSync,
  clearSyncError
} from '../store/slices/syncSlice.js';

// Activate the relativeTime plugin
dayjs.extend(relativeTime);

const ScheduleSync = () => {
  const dispatch = useDispatch();
  const { configs = [], status, syncingIds = {}, error } = useSelector((state) => state.bulkSync || {});

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const initialFormState = {
    name: '',
    companyId: '',
    sourceType: 'ZOHO',
    configType: 'ZOHO_TEMPLATE',
    cronExpression: '0 2 * * *',
    isActive: true,
    source: {
      identifier: '',
      credentials: { clientId: '', clientSecret: '', refreshToken: '', dc: 'com' }
    },
    process: { targetIdentifier: '' }
  };

  const [formData, setFormData] = useState(initialFormState);
  const [scheduleTime, setScheduleTime] = useState("02:00");
  const [frequency, setFrequency] = useState("daily");

  useEffect(() => { dispatch(fetchAllSyncConfigs()); }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearSyncError());
    }
  }, [error, dispatch]);

  /**
   * Helper: Translates Cron strings into friendly English
   */
  const formatCronLabel = (cron) => {
    if (!cron) return 'Not scheduled';
    const parts = cron.split(' ');
    
    // Every X minutes logic
    if (parts[0].startsWith('*/')) {
      return `Every ${parts[0].replace('*/', '')} mins`;
    }
    
    // Time-based (Daily/Weekly)
    if (parts.length >= 5) {
      try {
        const timeStr = dayjs().set('hour', parts[1]).set('minute', parts[0]).format('hh:mm A');
        if (parts[4] === '0') return `Sundays at ${timeStr}`;
        if (parts[4] === '*') return `Daily at ${timeStr}`;
      } catch (e) {
        return cron;
      }
    }
    return cron;
  };

  // Sync UI inputs (Time/Frequency) to the actual Cron string
  useEffect(() => {
    const [hours, minutes] = scheduleTime.split(':');
    let cron = `${parseInt(minutes)} ${parseInt(hours)} * * *`;
    if (frequency === 'weekly') cron = `${parseInt(minutes)} ${parseInt(hours)} * * 0`;
    setFormData(prev => ({ ...prev, cronExpression: cron }));
  }, [scheduleTime, frequency]);

  const handleRunTask = async (id) => {
    try {
      const result = await dispatch(runMasterSync(id)).unwrap();
      toast.success(result.message || "Manual sync started!");
    } catch (err) {
      toast.error("Manual sync failed");
    }
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditId(config.id);
      setFormData({
        ...initialFormState,
        ...config,
        source: {
          ...initialFormState.source,
          ...(config.source || {}),
          credentials: { ...initialFormState.source.credentials, ...(config.source?.credentials || {}) }
        },
        process: config.process || { targetIdentifier: '' }
      });
      
      const parts = config.cronExpression?.split(' ') || [];
      if (parts.length >= 2 && !parts[0].includes('*')) {
        setScheduleTime(`${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`);
        setFrequency(parts[4] === '0' ? 'weekly' : 'daily');
      }
    } else {
      setEditId(null);
      setFormData(initialFormState);
      setScheduleTime("02:00");
      setFrequency("daily");
    }
    setShowModal(true);
  };

  const updateCredentials = (field, value) => {
    setFormData(prev => ({
      ...prev,
      source: {
        ...prev.source,
        credentials: { ...prev.source.credentials, [field]: value }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await dispatch(updateSyncConfig({ id: editId, dto: formData })).unwrap();
      else await dispatch(createSyncConfig(formData)).unwrap();
      setShowModal(false);
      toast.success("Configuration saved successfully!");
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    if (window.confirm("Permanently delete this configuration?")) {
      try {
        await dispatch(deleteSyncConfig(id)).unwrap();
        toast.info("Config removed");
      } catch (err) {}
    }
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <style>{`
        .custom-table-header th { background-color: #0f172a !important; color: #f8fafc !important; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; border: none !important; }
        .glass-container { background: white; border-radius: 12px; border: 1px solid #e2e8f0; }
        .schedule-badge { background: #eff6ff; color: #1e40af; font-weight: 600; font-size: 0.75rem; padding: 4px 10px; border-radius: 6px; border: 1px solid #dbeafe; }
      `}</style>

      <div className="container-fluid py-4 px-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold m-0" style={{ letterSpacing: "-1px" }}>Schedule Sync</h2>
            <p className="text-muted small mb-0">Automated Zoho Data Loops</p>
          </div>
          <button className="btn btn-dark px-4 rounded-3 fw-bold" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-lg me-2"></i>New Task
          </button>
        </div>

        <div className="mb-4">
          <input 
            type="text" 
            className="form-control border-0 shadow-sm px-4" 
            placeholder="Search tasks..." 
            style={{ borderRadius: "10px", height: '45px' }} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>

        <div className="glass-container overflow-hidden shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Config Details</th>
                <th className="py-3 text-center">Schedule</th>
                <th className="py-3 text-center">Last Run</th>
                <th className="pe-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((config) => (
                <tr key={config.id} className="align-middle">
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>{config.name}</div>
                    <div className="text-muted font-monospace" style={{ fontSize: "0.65rem" }}>{config.companyId}</div>
                  </td>
                  <td className="text-center">
                    <div className="schedule-badge">
                      <i className="bi bi-calendar-event me-2"></i>
                      {formatCronLabel(config.cronExpression)}
                    </div>
                  </td>
                  <td className="text-center text-muted" style={{ fontSize: "0.75rem" }}>
                    {config.lastRunAt ? dayjs(config.lastRunAt).fromNow() : 'Never'}
                  </td>
                  <td className="pe-4 text-end">
                    <div className="d-flex justify-content-end gap-2">
                      <button className="btn btn-sm btn-outline-dark" onClick={() => handleOpenModal(config)}>Edit</button>
                      <button className="btn btn-sm btn-success" onClick={() => handleRunTask(config.id)} disabled={syncingIds[config.id] === 'running'}>
                        {syncingIds[config.id] === 'running' ? 'Running...' : 'Run Now'}
                      </button>
                      <button className="btn btn-sm btn-light" onClick={() => handleDelete(config.id)}><i className="bi bi-trash"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 }}>
          <div className="bg-white rounded-4 shadow-lg overflow-hidden" style={{ width: "550px", maxHeight: '95vh', overflowY: 'auto' }}>
            <div className="bg-dark text-white p-3 px-4 d-flex justify-content-between align-items-center">
              <h6 className="m-0 fw-bold">Zoho Configuration</h6>
              <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 bg-light">
              <div className="row g-3">
                <div className="col-12">
                  <label className="fw-bold small text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>General</label>
                  <input className="form-control mb-2" placeholder="Task Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  <input className="form-control font-monospace small" placeholder="Company UUID" value={formData.companyId} onChange={(e) => setFormData({...formData, companyId: e.target.value})} required />
                </div>

                <div className="col-md-6">
                  <label className="fw-bold small text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Start Time</label>
                  <input type="time" className="form-control" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="fw-bold small text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Frequency</label>
                  <select className="form-select" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly (Sunday)</option>
                  </select>
                </div>

                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 border">
                    <label className="fw-bold small text-primary text-uppercase mb-2 d-block" style={{ fontSize: '0.65rem' }}>Zoho Credentials</label>
                    <input className="form-control form-control-sm mb-2" placeholder="Client ID" value={formData.source?.credentials?.clientId || ''} onChange={(e) => updateCredentials('clientId', e.target.value)} required />
                    <input className="form-control form-control-sm mb-2" placeholder="Client Secret" type="password" value={formData.source?.credentials?.clientSecret || ''} onChange={(e) => updateCredentials('clientSecret', e.target.value)} required />
                    <textarea className="form-control form-control-sm mb-2" placeholder="Refresh Token" value={formData.source?.credentials?.refreshToken || ''} onChange={(e) => updateCredentials('refreshToken', e.target.value)} required rows="2" />
                    <div className="row g-2">
                      <div className="col-6">
                        <select className="form-select form-select-sm" value={formData.source?.credentials?.dc || 'com'} onChange={(e) => updateCredentials('dc', e.target.value)}>
                          <option value="com">.com (US)</option><option value="in">.in (India)</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <input className="form-control form-control-sm" placeholder="Identifier" value={formData.process?.targetIdentifier || ''} onChange={(e) => setFormData({...formData, process: { targetIdentifier: e.target.value }})} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="p-2 border border-dashed rounded text-center bg-white">
                    <small className="text-muted d-block" style={{ fontSize: '10px' }}>PREVIEW CRON</small>
                    <code className="text-primary fw-bold">{formData.cronExpression}</code>
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} />
                    <label className="form-check-label small fw-bold">Active Schedule</label>
                  </div>
                </div>

                <div className="col-12 pt-2">
                  <button type="submit" className="btn btn-dark w-100 rounded-pill fw-bold py-2 shadow" disabled={status === 'loading'}>
                    {status === 'loading' ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ScheduleSync;